import { BaseTable, Button, Card, FooterMenu, HeaderMenu, TableHeader } from "components"
import { convertDate } from "pages/Inventori/Transaction/TransactionJournal/utils"
import { IoSendSharp } from "react-icons/io5"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { APPROVAL, useHandleAuth, useLocation } from "utils"
import { useTransferItemsList } from "../../../utils/hooks"
import { PERMISION } from "security"

function getTransferStatusLabel(status: number) {
  if (status === 1) return "OPEN"
  if (status === 2) return "CLOSED"
  if (status === 3) return "COMPLETED"
  return "-"
}

export const TableTransferList = () => {
  const navigate = useNavigate()
  const transfers = useTransferItemsList({ Approval: APPROVAL.APPROVED })
  const handleAuth = useHandleAuth()
  const user = handleAuth.userTokenData()
  const { data: locationOptions } = useLocation()

  const workLocationLabel = useMemo(() => {
    if (!user) return ""
    if (user.location_name) return user.location_name
    if (user.location_id == null) return ""
    const match = locationOptions.find((o) => o.value === String(user.location_id))
    return match?.label ?? ""
  }, [user, locationOptions])

  const visibleTransfers = useMemo(() => {
    const list = transfers?.data ?? []
    const isFullAccess = Boolean((user as any)?.all_access)
    const isSuperAdmin = Boolean((user as any)?.is_admin && (user as any)?.is_subscribed)

    // Full Access and Super Admin can see all destination locations.
    if (isFullAccess || isSuperAdmin) return list

    // Fallback to all data when user location is not set.
    if (user?.location_id == null) return list

    const userLocationId = Number(user.location_id)
    if (Number.isNaN(userLocationId)) return list

    return list.filter((item) => Number(item.location_to_id) === userLocationId)
  }, [transfers?.data, user])

  return (
    <section className="container my-2">
      <HeaderMenu title="RECEIVED INVENTORY" /> 
      
      <Card className="my-2">
        <Card.Body>
          <div className="flex flex-col justify-start items-start uppercase">
            <span className="font-bold text-sm">
              EMPLOYEE : {user?.employee_name ?? ""}
            </span>

            <span className="font-bold text-sm">
              LOCATION : {workLocationLabel}
            </span>
          </div>
        </Card.Body>
      </Card>

      <BaseTable>
        <TableHeader className="uppercase">
          <tr>
            <th>No</th>
            <th colSpan={2}>TRANSFER ID#</th>
            <th>Date</th>
            <th>Source Location</th>
            <th>Destination Location</th>
            <th>Trader</th>
            <th>Memo</th>
            <th>Qty Transferred</th>
            <th>Status</th>
          </tr>
        </TableHeader>

        <tbody>
          {visibleTransfers.length ? (
            visibleTransfers.map((transfer, key) => {
              return (
                <tr key={transfer.transfer_id}>
                  <td>{key + 1}</td>
                  <td className="text-center px-0">
                    <Button
                      permission={PERMISION.INVENTORY.TRANSACTION.CREATE_RECEIVED_ITEM.UPDATE}
                      size="xs"
                      color="ghost"
                      onClick={() => {
                        navigate(
                          `/inventory/transaction/received/${transfer.transfer_id}`
                        )
                      }}>
                      <IoSendSharp className="size-4" />
                    </Button>
                  </td>
                  <td>{transfer.trx_code}</td>
                  <td>{convertDate(transfer.transaction_date)}</td>
                  <td>{transfer.location_from_name}</td>
                  <td>{transfer.location_to_name}</td>
                  <td>{transfer.employee_name}</td>
                  <td>{transfer.memo}</td>
                  <td>{transfer.total_transferred ?? 0}</td>
                  <td>{getTransferStatusLabel(transfer.transfer_status)}</td>
                </tr>
              )
            })
          ) : (
            <tr>
              <td colSpan={10} className="text-center">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </BaseTable>
      <section className="mt-2">
        <FooterMenu />
      </section>
    </section>
  )
}
